import { useState, useEffect } from 'react';
import { FeatureCollection, Feature, Geometry, GeoJsonProperties } from 'geojson';
import { FetchRequestTypes, useFetch } from '../../../hooks/useFetch';
import { CmsMapData } from './useCmsMapData';
import { Dataset } from '@mapbox/mapbox-sdk/services/datasets';

export const useMapboxData = (customMapLayers: mapboxgl.AnyLayer[], currentCmsMapData: CmsMapData | null) => {
  const [datasetListUrl, setDatasetListUrl] = useState<string | null>(null);
  const [datasetUrl, setDatasetUrl] = useState<string[] | null>(null);
  const [allDatasets, setAllDatasets] = useState<Dataset[] | null>(null);
  const [mapBoxFeatures, setMapBoxFeatures] = useState<Feature<Geometry, GeoJsonProperties>[] | null>(null);
  const [loading, setLoading] = useState(false);

  const {
    data: datasetList,
    loading: datasetListLoading,
    error: datasetListError,
  } = useFetch(datasetListUrl, FetchRequestTypes.GET);

  const { data: datasets, loading: datasetsLoading, error: datasetsError } = useFetch(datasetUrl, FetchRequestTypes.GET);

  // -------------------------------------------------------
  // USE EFFECT - Load dataset list from MapBox on mount
  // -------------------------------------------------------

  useEffect(() => {
    if (currentCmsMapData) {
      setLoading(true);
      setDatasetListUrl(`/api-mapbox`);
    }
  }, [currentCmsMapData]);

  useEffect(() => {
    if (datasetList) {
      setAllDatasets(datasetList);
    }
  }, [datasetList]);

  // -------------------------------------------------------
  // USE EFFECT - load each node/feature data from mapbox
  // -------------------------------------------------------

  useEffect(() => {
    if (customMapLayers && allDatasets && currentCmsMapData) {
      const datasetArray = allDatasets as Dataset[];
      const featureUrlArray: string[] = [];

      if (customMapLayers.length > 0) {
        customMapLayers.forEach((mapLayer) => {
          const sourceName = (mapLayer as any)['source-layer'];
          const dataset = datasetArray.find((data) => data.name === sourceName);
          if (dataset) {
            const url = `/api-mapboxdataset/${dataset.id}/features?access_token=pk.eyJ1IjoicG9ydGdvdGNvbSIsImEiOiJjbGhrN3F5dGUwNWVoM2VueG51cW5uaTJpIn0.Wp4hSyeLAqbD5dYBbniUAw`;
            featureUrlArray.push(url);
          }
        });

        if (featureUrlArray.length > 0) {
          setDatasetUrl(featureUrlArray);
        }
      }
    }
  }, [customMapLayers, allDatasets, currentCmsMapData]);

  // -------------------------------------------------------
  // USE EFFECT - data loading is complete
  // -------------------------------------------------------

  useEffect(() => {
    if (datasets && !datasetListLoading && !datasetsLoading) {
      //Flatten all features of all map layers into one array
      const allMapBoxFeatures: Feature<Geometry, GeoJsonProperties>[] =
        (datasets as FeatureCollection[]).map((dataset) => dataset.features).flat() || [];

      setMapBoxFeatures(allMapBoxFeatures);

      setLoading(false);
    }
  }, [datasets]);

  // -------------------------------------------------------
  // USE EFFECT - data error handling
  // -------------------------------------------------------

  useEffect(() => {
    if (datasetListError || datasetsError) {
      console.error('Error: loading mapbox data');
      setLoading(false);
    }
  }, [datasetListError, datasetsError]);

  return { mapBoxFeatures, loading };
};

